import './index.css';
import { useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from 'hooks';

/**
 * Custom cursor — a small sapphire dot with a lagging outer ring.
 * Only mounts its rAF loop on non-reduced-motion, fine-pointer devices.
 * The element is always aria-hidden and pointer-events: none.
 */
const Cursor = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const cursorRef = useRef(null);
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    // Guard: only run on fine-pointer hover-capable devices
    const isFinePointer = window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches;
    if (!isFinePointer) return;

    let rafId;
    // Actual mouse position (updated immediately on mousemove)
    const mouse = { x: 0, y: 0 };
    // Lagged ring position (lerped toward mouse each frame)
    const ring = { x: 0, y: 0 };
    // Whether cursor has ever moved (start hidden)
    let visible = false;

    const LERP = prefersReducedMotion ? 1 : 0.12;

    const onMouseMove = e => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;

      if (!visible) {
        // Snap ring to mouse on first move so it doesn't fly in from (0,0)
        ring.x = mouse.x;
        ring.y = mouse.y;
        cursor.style.opacity = '1';
        visible = true;
      }

      // Dot follows exactly
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${mouse.x}px, ${mouse.y}px) translate(-50%, -50%)`;
      }
    };

    const HOVER_SELECTOR = 'a, button, [role="button"], input, label, .ed-projects__row';

    const onMouseOver = e => {
      if (e.target.closest(HOVER_SELECTOR)) {
        cursor.classList.add('cursor--hover');
      }
    };

    const onMouseOut = e => {
      if (e.target.closest(HOVER_SELECTOR)) {
        cursor.classList.remove('cursor--hover');
      }
    };

    const loop = () => {
      // Lerp ring toward mouse
      ring.x += (mouse.x - ring.x) * LERP;
      ring.y += (mouse.y - ring.y) * LERP;

      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ring.x}px, ${ring.y}px) translate(-50%, -50%)`;
      }

      rafId = requestAnimationFrame(loop);
    };

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseover', onMouseOver, { passive: true });
    document.addEventListener('mouseout', onMouseOut, { passive: true });
    rafId = requestAnimationFrame(loop);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      cancelAnimationFrame(rafId);
    };
  }, [prefersReducedMotion]);

  return (
    <div
      ref={cursorRef}
      className="cursor"
      aria-hidden="true"
      style={{ opacity: 0 }}
    >
      <div ref={dotRef} className="cursor__dot" />
      <div ref={ringRef} className="cursor__ring" />
    </div>
  );
};

export default Cursor;
